import React, { useState, useEffect, useCallback } from 'react';
import './ItemsDisplay.scss';
import Table from './components/Table/Table';
import CheckboxMultiSelect from '../../../../../../components/common/CheckboxMultiSelect/CheckboxMultiSelect';
import { Offcanvas } from 'react-bootstrap';
import { X, Search } from 'react-bootstrap-icons';
import { FaPlus, FaMousePointer, FaTimes } from 'react-icons/fa';
import { Item } from '../../types';

interface ItemsTableProps {
  items: Item[];
  setSelectedCategory?: any;
  categoryId: string | undefined;
  showMobileFilters?: boolean;
  setShowMobileFilters?: (show: boolean) => void;
}

interface FilterOption {
  value: string | number;
  label: string;
}

const ItemsDisplay: React.FC<ItemsTableProps> = ({
  items,
  setSelectedCategory,
  categoryId,
  showMobileFilters,
  setShowMobileFilters,
}) => {
  const [originalItems, setOriginalItems] = useState<Item[]>(items);
  const [filteredItems, setFilteredItems] = useState<Item[]>(items);
  const [localShowMobileFilters, setLocalShowMobileFilters] = useState(false);

  // filter states (mirror Transactions structure)
  const [selectedCategories, setSelectedCategories] = useState<FilterOption[]>([]);
  const [showOnlyActive, setShowOnlyActive] = useState(false);
  const [showOnlyInactive, setShowOnlyInactive] = useState(false);
  const [showOnlyWithStock, setShowOnlyWithStock] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const [availableCategories, setAvailableCategories] = useState<any[]>([]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(true);
  const tableActionsRef = React.useRef<any>(null);
  const [actionsVersion, setActionsVersion] = useState(0);

  // Use external state if provided, otherwise local
  const isFiltersOpen = showMobileFilters !== undefined ? showMobileFilters : localShowMobileFilters;
  const toggleFilters = setShowMobileFilters || setLocalShowMobileFilters;

  useEffect(() => {
    setOriginalItems(items);
    setFilteredItems(items);
  }, [items]);

  // Load categories
  useEffect(() => {
    const load = async () => {
      try {
        setLoadingCategories(true);
        const { getAllItemHolderCategoriesByLocalId } = await import('../../../../../../crud/menu_api/categories');
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const localId = user.local_id || 0;
        const resp = await getAllItemHolderCategoriesByLocalId(localId);
        setAvailableCategories(resp?.data || []);
      } catch (e) {
        console.error('Error loading categories', e);
      } finally {
        setLoadingCategories(false);
      }
    };
    load();
  }, []);

  // register table actions when Table mounts
  const registerTableActions = (actions: any) => {
    tableActionsRef.current = actions;
    setActionsVersion(v => v + 1);
  };

  const applyFilters = useCallback(() => {
    let filtered = originalItems;

    if (selectedCategories.length > 0) {
      const ids = selectedCategories.map(c => String(c.value));
      filtered = filtered.filter(it => ids.includes(String(it.category_id)));
    }

    if (showOnlyActive) filtered = filtered.filter(it => it.state === true);
    if (showOnlyInactive) filtered = filtered.filter(it => it.state === false);
    if (showOnlyWithStock) filtered = filtered.filter(it => it.stock_control === true);

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(it =>
        (it.item_name || '').toLowerCase().includes(q) ||
        (it.label || '').toLowerCase().includes(q)
      );
    }

    setFilteredItems(filtered);
  }, [originalItems, selectedCategories, showOnlyActive, showOnlyInactive, showOnlyWithStock, searchQuery]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const categoryOptions = availableCategories.map((c: any) => ({ value: c.category_id, label: c.category_name }));
  const stateOptions = [
    { value: 'active', label: 'Solo activos' },
    { value: 'inactive', label: 'Solo inactivos' },
  ];
  const stockOptions = [{ value: 'with_stock', label: 'Con control de stock' }];

  const handleStateFilterChange = (selected: FilterOption[]) => {
    const hasActive = selected.some(s => s.value === 'active');
    const hasInactive = selected.some(s => s.value === 'inactive');
    setShowOnlyActive(hasActive);
    setShowOnlyInactive(hasInactive);
  };

  const handleStockFilterChange = (selected: FilterOption[]) => {
    setShowOnlyWithStock(selected.some(s => s.value === 'with_stock'));
  };

  const handleClearSearch = () => setSearchQuery('');

  return (
    <div className="items-display-container">

      <div className='filter-no-reponsive'>
        <div className="transactions-filters-main-container">
          <div className="responsive-filters-container">
            <div className="mp-toolbar items-toolbar">
              <div className="search-section">
                <div className="custom-search-bar">
                  <Search className="search-icon" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder="Buscar ítems..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button className="clear-search-btn" onClick={handleClearSearch} aria-label="Limpiar búsqueda">
                      <X />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                <CheckboxMultiSelect options={categoryOptions} value={selectedCategories} onChange={setSelectedCategories} placeholder="Categorías" />
                <CheckboxMultiSelect options={stateOptions} value={showOnlyActive || showOnlyInactive ? (showOnlyActive ? [{value:'active', label:'Solo activos'}] : [{value:'inactive', label:'Solo inactivos'}]) : []} onChange={handleStateFilterChange} placeholder="Estado" />
                <CheckboxMultiSelect options={stockOptions} value={showOnlyWithStock ? stockOptions : []} onChange={handleStockFilterChange} placeholder="Stock" />
              </div>

              <div className="flex items-center gap-2 ml-auto flex-shrink-0">
                <button className="mp-btn-primary" onClick={() => tableActionsRef.current?.openCreate?.()}>
                  <FaPlus />
                  <span>Crear</span>
                </button>

                <button className="mp-toolbar-btn" onClick={() => tableActionsRef.current?.toggleSelection?.()}>
                  {tableActionsRef.current?.getSelectedCount?.() > 0 ? (
                    <>
                      <FaTimes />
                      <span>Cancelar</span>
                    </>
                  ) : (
                    <>
                      <FaMousePointer />
                      <span>Seleccionar</span>
                    </>
                  )}
                </button>

                {tableActionsRef.current?.getSelectedCount?.() > 0 && (
                  <button className="mp-btn-danger" onClick={() => tableActionsRef.current?.deleteSelection?.()}>
                    Eliminar ({tableActionsRef.current?.getSelectedCount?.()})
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Offcanvas show={isFiltersOpen} onHide={() => toggleFilters(false)} placement="end" className="mp-offcanvas items-offcanvas" backdrop={true} scroll={false}>
        <Offcanvas.Header className="offcanvas-header">
          <div className="header-content">
            <Offcanvas.Title className="offcanvas-title">Opciones de Filtrado</Offcanvas.Title>
            <div className="section-indicator">Ítems</div>
          </div>
          <button className="close-button" onClick={() => toggleFilters(false)} aria-label="Cerrar filtros">
            <X size={24} />
          </button>
        </Offcanvas.Header>
        <Offcanvas.Body className="offcanvas-body">
          <div className="filters-container">
            <div className="filter-section">
              <h6 className="filter-section-title">Filtros Generales</h6>

              <div className="filter-group">
                <label className="filter-label">Buscar</label>
                <input type="text" className="custom-input" placeholder="Buscar ítems..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
              </div>

              <div className="filter-group">
                <CheckboxMultiSelect options={categoryOptions} value={selectedCategories} onChange={setSelectedCategories} placeholder="Categorías" />
              </div>

              <div className="filter-group">
                <CheckboxMultiSelect options={stateOptions} value={showOnlyActive || showOnlyInactive ? (showOnlyActive ? [{value:'active', label:'Solo activos'}] : [{value:'inactive', label:'Solo inactivos'}]) : []} onChange={handleStateFilterChange} placeholder="Estado" />
              </div>

              <div className="filter-group">
                <CheckboxMultiSelect options={stockOptions} value={showOnlyWithStock ? stockOptions : []} onChange={handleStockFilterChange} placeholder="Stock" />
              </div>
            </div>
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      <Table
        items={filteredItems}
        setData={setFilteredItems}
        originalItems={originalItems}
        categoryId={categoryId}
        setOriginalItems={setOriginalItems}
        allItems={items}
        onGoBack={setSelectedCategory ? () => setSelectedCategory(null) : undefined}
        showMobileFilters={isFiltersOpen}
        onCloseMobileFilters={() => toggleFilters(false)}
        hasOriginalItems={originalItems.length > 0}
        onRegisterActions={registerTableActions}
      />
    </div>
  );
};

export default ItemsDisplay;
